import { Autocomplete, Stack, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useMemo } from 'react';

import { EsriDma, EsriRegions, listEsriDmas, StateOption } from '../../../../api/ach/esri';
import { useConfig } from '../../../../hooks/useConfig';
import US_STATES from './usStates';

interface Props {
  regions: EsriRegions;
  onChange: (regions: EsriRegions) => void;
  disabled?: boolean;
}

function EsriRegionInput({ regions, onChange, disabled }: Props) {
  const { achApiHost } = useConfig();
  const {
    data: dmaOptions = [],
    isLoading: dmasLoading,
    isError: dmasError,
  } = useQuery({
    queryKey: ['esriDmas', achApiHost],
    queryFn: () => listEsriDmas(achApiHost),
    staleTime: Infinity,
    retry: false,
  });

  const selectedStates = useMemo(() => US_STATES.filter((s) => regions.states.includes(s.code)), [regions.states]);
  const selectedDmas = useMemo(
    () => dmaOptions.filter((d) => regions.dmas.includes(d.dma_id)),
    [dmaOptions, regions.dmas]
  );

  return (
    <Stack spacing={1.5}>
      <Autocomplete<StateOption, true>
        multiple
        size="small"
        options={US_STATES}
        value={selectedStates}
        getOptionLabel={(opt) => opt.name}
        isOptionEqualToValue={(a, b) => a.code === b.code}
        disabled={disabled}
        onChange={(_, value) => onChange({ ...regions, states: value.map((v) => v.code) })}
        // eslint-disable-next-line react/jsx-props-no-spreading
        renderInput={(params) => <TextField {...params} label="States" placeholder="Pick states" />}
      />
      <Autocomplete<EsriDma, true>
        multiple
        size="small"
        options={dmaOptions}
        value={selectedDmas}
        loading={dmasLoading}
        getOptionLabel={(opt) => opt.name}
        isOptionEqualToValue={(a, b) => a.dma_id === b.dma_id}
        disabled={disabled || dmasError}
        onChange={(_, value) => onChange({ ...regions, dmas: value.map((v) => v.dma_id) })}
        renderInput={(params) => (
          <TextField
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
            label="DMAs"
            placeholder={dmasError ? 'Failed to load DMAs' : 'Search by city / market name'}
            error={dmasError}
          />
        )}
      />
      <Autocomplete<string, true, false, true>
        multiple
        freeSolo
        size="small"
        options={[]}
        value={regions.zips}
        disabled={disabled}
        onChange={(_, value) => {
          // Each entry may be a pasted list, so split before keeping the 5-digit ones.
          const zips = value.flatMap((v) => v.split(/[\s,;]+/)).filter((v) => /^\d{5}$/.test(v));
          onChange({ ...regions, zips: Array.from(new Set(zips)) });
        }}
        renderInput={(params) => (
          <TextField
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
            label="ZIP codes"
            placeholder="Type or paste 5-digit ZIPs, then press Enter"
          />
        )}
      />
    </Stack>
  );
}

export default EsriRegionInput;
